import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Leaf, Drumstick, PackageX } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { getMenu, saveMenuItem, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { cn } from "@/lib/utils";

export default function Inventory() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["menu"], queryFn: async () => (await getMenu()).data });
  const items = Array.isArray(data) ? data : [];

  const mut = useMutation({
    mutationFn: (it) =>
      saveMenuItem({
        id: it.id,
        name: it.name,
        price: it.price,
        description: it.description || "",
        is_veg: it.is_veg !== false,
        available: !it.available,
        variants: (it.variants || []).length ? it.variants : null,
      }),
    onMutate: async (it) => {
      await qc.cancelQueries({ queryKey: ["menu"] });
      const prev = qc.getQueryData(["menu"]);
      qc.setQueryData(["menu"], (old) =>
        (old || []).map((x) => (x.id === it.id ? { ...x, available: !x.available } : x))
      );
      return { prev };
    },
    onSuccess: (_r, it) => toast.success(`${it.name} marked ${!it.available ? "in stock" : "out of stock"}`),
    onError: (e, _it, ctx) => {
      qc.setQueryData(["menu"], ctx?.prev);
      toast.error(errMsg(e));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["menu"] }),
  });

  const outCount = items.filter((i) => i.available === false).length;

  return (
    <div className="p-5 sm:p-8 max-w-3xl">
      <PageHeader title="Inventory" subtitle="Flip items in / out of stock instantly" />

      {outCount > 0 && (
        <div className="flex items-center gap-2.5 rounded-md border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700 mb-5" data-testid="out-of-stock-summary">
          <PackageX className="h-4 w-4" />
          {outCount} item(s) currently out of stock and hidden from customers.
        </div>
      )}

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border py-16 text-center text-muted-foreground">
          No menu items yet. Add items from the Menu page first.
        </div>
      ) : (
        <div className="rounded-md border border-border bg-card divide-y divide-border">
          {items.map((it) => {
            const inStock = it.available !== false;
            return (
              <div key={it.id} className="flex items-center justify-between px-4 py-3.5" data-testid={`inventory-row-${it.id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  {it.is_veg !== false ? (
                    <Leaf className="h-4 w-4 text-emerald-600 shrink-0" />
                  ) : (
                    <Drumstick className="h-4 w-4 text-red-600 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="font-medium truncate">{it.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{inr(it.price)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={cn("text-xs font-semibold", inStock ? "text-emerald-600" : "text-red-600")}>
                    {inStock ? "IN STOCK" : "OUT OF STOCK"}
                  </span>
                  <Switch
                    checked={inStock}
                    disabled={mut.isPending}
                    onCheckedChange={() => mut.mutate(it)}
                    data-testid={`inventory-toggle-${it.id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
